/**
 * HTTP-backed implementation of `OnFilePort` (TG-4, m1-exposure-detail-
 * sourcing), over the published portal API route this feature's own TG-3
 * built: `GET /api/portal/on-file`. Mirrors `../renewal-summary-client.ts`'s
 * shape — see that file's own header comment for the outcome-mapping
 * rationale this file repeats without re-explaining it.
 *
 * ADR-20261018-1skz: gated by `whenSessionTokenHeld` — no request fires
 * while no session token is held; a call made before sign-in waits for one
 * to appear via `subscribeTokenChange` rather than firing immediately.
 */
import { authenticatedFetch } from "../../portal-auth/authenticated-fetch";
import type { ReadOutcome } from "../outcome";
import type { OnFilePort, OnFileReadResult } from "../on-file-port";
import { whenSessionTokenHeld } from "./session-gate";

const ON_FILE_ROUTE = "/api/portal/on-file";

function failedOutcome(httpStatus: number, message: string): ReadOutcome<OnFileReadResult> {
  return { status: "Failed", detail: { httpStatus, message } };
}

/**
 * Reads the decoded JSON body's `sections` field. The published contract
 * (`src/api/contracts/on-file.openapi.yaml`) is this field's own source of
 * truth, vendored and diffed by `state/contracts/on-file.contract.json`
 * and its `conformance.test.ts`, not re-validated a second time here.
 */
async function readSectionsField(response: Response): Promise<OnFileReadResult> {
  const body = (await response.json()) as { sections?: OnFileReadResult["sections"] };
  if (body.sections === undefined) {
    throw new Error(`GET ${ON_FILE_ROUTE}: 200 response carried no readable "sections" field.`);
  }
  return { sections: body.sections };
}

async function performReadOnFile(): Promise<ReadOutcome<OnFileReadResult>> {
  let response: Response;
  try {
    response = await authenticatedFetch(ON_FILE_ROUTE, { method: "GET" });
  } catch (error) {
    // A thrown fetch (no response at all) is a dropped network connection
    // (FIND-35/OBJ-20's trigger) — maps straight to Failed; a read has no
    // Indeterminate case (ADR-20260918-gvy0).
    return failedOutcome(0, error instanceof Error ? error.message : String(error));
  }

  if (response.status !== 200) {
    return failedOutcome(response.status, `GET ${ON_FILE_ROUTE} returned ${response.status}.`);
  }

  try {
    const value = await readSectionsField(response);
    return { status: "Succeeded", value };
  } catch (error) {
    return failedOutcome(response.status, error instanceof Error ? error.message : String(error));
  }
}

export function createHttpOnFilePort(): OnFilePort & { isSampleData: false } {
  return {
    isSampleData: false,
    async readOnFile(): Promise<ReadOutcome<OnFileReadResult>> {
      return whenSessionTokenHeld(performReadOnFile);
    },
  };
}
